import { defineStore } from "pinia";
import { GeoPackageService } from "../services/geopackage-service";
import { GeoPackageStyle } from "../utils/geopackage-style-utils";

export interface GeoPackageLayer {
  id: number;
  geopackage_id: number;
  layer_name: string;
  geometry_type: string;
  feature_count: number;
  visible: boolean;
  style?: GeoPackageStyle;
}

export interface GeoPackageFile {
  id: number;
  file_name: string;
  upload_date: string;
  file_size: number;
  layers: GeoPackageLayer[];
}

export const useGeoPackageStore = defineStore("geoPackage", {
  state: () => ({
    files: [] as GeoPackageFile[],
    geoPackageLayers: [] as GeoPackageLayer[],
    layersByFileId: {},
    selectedFile: null as GeoPackageFile | null,
    selectedLayers: [] as GeoPackageLayer[],
    isLoading: false,
    error: null as string | null,
  }),

  getters: {
    getFiles: (state) => state.files,
    getSelectedFile: (state) => state.selectedFile,
    getSelectedLayers: (state) => state.selectedLayers,
    getGeoPackageLayers: (state) => state.geoPackageLayers,
    getLayersForFile: (state) => (fileId: number) => {
      return state.layersByFileId[fileId] || [];
    },
    getIsLoading: (state) => state.isLoading,
    getError: (state) => state.error,
  },

  actions: {
    async fetchGeoPackages(projectId: number) {
      // console.log(
      //   "fetchGeoPackages in geoPackageStore with projectId: ",
      //   projectId,
      // );
      this.isLoading = true;
      this.error = null;

      try {
        const response: any = await GeoPackageService.getGeoPackages(projectId);
        // console.log("response in fetchGeoPackages: ", response);

        this.files = (response.data || response) as GeoPackageFile[];

        // Clear layers for files that no longer exist
        const fileIds = this.files.map((file) => file.id);
        Object.keys(this.layersByFileId).forEach((id) => {
          if (!fileIds.includes(Number(id))) {
            delete this.layersByFileId[id];
          }
        });

        return this.files;
      } catch (error) {
        this.error = error.message;
        console.error("Error fetching GeoPackage files:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchGeoPackageLayers(fileId: number) {
      this.isLoading = true;
      this.error = null;

      try {
        const response: any =
          await GeoPackageService.getGeoPackageLayers(fileId);
        // console.log("response in fetchGeoPackageLayers: ", response);

        const layers = (response.data || response) as GeoPackageLayer[];
        this.geoPackageLayers = layers;

        // Organize layers by file ID
        this.layersByFileId[fileId] = layers;

        return layers;
      } catch (error) {
        this.error = error.message;
        console.error("Error fetching GeoPackage layers:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async uploadGeoPackage(
      file: File,
      projectId: number,
      overwriteExisting = false,
    ) {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await GeoPackageService.uploadGeoPackage(
          file,
          projectId,
          overwriteExisting,
        );
        // console.log("response in uploadGeoPackage: ", response);

        await this.fetchGeoPackages(projectId);
        return response;
      } catch (error) {
        this.error = error.message;
        console.error("Error uploading GeoPackage:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async selectFile(fileId: number) {
      this.isLoading = true;
      this.error = null;
      // console.log("selectFile in geoPackageStore with fileId: ", fileId);
      // console.log("this.files in selectFile in geoPackageStore: ", this.files);
      try {
        const file = this.files.find((f) => f.id === fileId);
        // console.log("file in selectFile in geoPackageStore: ", file);
        if (!file) throw new Error("File not found");

        const response: any = await GeoPackageService.getGeoPackageLayers(
          fileId,
          true,
          true,
        );
        //console.log("response in selectFile in geoPackageStore: ", response);
        file.layers = (response.data || response) as GeoPackageLayer[];
        this.selectedFile = file;
      } catch (error) {
        this.error = error.message;
        console.error("Error loading GeoPackage layers:", error);
      } finally {
        this.isLoading = false;
      }
    },

    selectLayers(layerIds: number[]) {
      // Reset the selected layers array
      this.selectedLayers = [];

      // If we have no layer IDs, just return
      if (layerIds.length === 0) return;

      // If a file is already selected, use its layers
      if (this.selectedFile) {
        this.selectedLayers = this.selectedFile.layers.filter((layer) =>
          layerIds.includes(layer.id),
        );
        return;
      }

      // Otherwise, search for layers across all files
      for (const file of this.files) {
        const fileLayers = this.layersByFileId[file.id] || [];
        const matchingLayers = fileLayers.filter((layer) =>
          layerIds.includes(layer.id),
        );

        if (matchingLayers.length > 0) {
          this.selectedLayers.push(...matchingLayers);
        }
      }

      console.log(
        `Found ${this.selectedLayers.length} layers out of ${layerIds.length} requested IDs`,
      );
    },

    clearSelection() {
      this.selectedFile = null;
      this.selectedLayers = [];
    },
  },
});
